import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { GroupContractDto } from 'api/contract';
import { contractsAPI } from 'services/swagger-api';
import {
  GROUP_CONTRACT_NOTIFICATIONS_CONFIG,
  useSignalRNotifications,
} from 'shared/signalR/hooks/useSignalRNotifications';
import { getGroupContractProcess } from '../api/getGroupContractProcess';
import { GroupContractProcessResponse, GroupContractStepIdentifier } from '../types/enums';
import { buildProcessRoute, detectProcessPrefix } from '../utils/getRouteByStep';
import { GroupContractProcessContext } from './GroupContractProcessContext';

interface GroupContractProcessProviderProps {
  groupContractId: string;
  children: ReactNode;
}

export const GroupContractProcessProvider = ({
  groupContractId,
  children,
}: GroupContractProcessProviderProps) => {
  const { pathname } = useLocation();
  const routePrefix = useMemo(() => detectProcessPrefix(pathname), [pathname]);
  const [process, setProcess] = useState<GroupContractProcessResponse | null>(null);
  const [contract, setContract] = useState<GroupContractDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dataVersion, setDataVersion] = useState(0);

  // hasDataRef : distingue le chargement initial (page de loading) des refresh
  // suivants (mise à jour en place, aucun démontage → pas de flash ni de perte
  // d'état local dans les cards).
  const hasDataRef = useRef(false);

  const refresh = useCallback(async () => {
    try {
      // Process + contract details en parallèle — source unique du GroupContractDto :
      // les cards le consomment via le contexte au lieu de le refetch chacune.
      const [processData, contractData] = await Promise.all([
        getGroupContractProcess(groupContractId),
        contractsAPI.getGroupContractByID({ groupContractId }),
      ]);
      setProcess(processData);
      setContract(contractData);
      setError(null);
      hasDataRef.current = true;
      // Signale aux données annexes (documents, statut signature, mandats)
      // qu'elles doivent se recharger.
      setDataVersion(v => v + 1);
    } catch (err: any) {
      console.error('Failed to load group contract process', err);
      // Un échec de refresh en arrière-plan ne doit pas blanchir une page
      // déjà affichée — l'erreur n'est montrée que si rien n'est encore chargé.
      if (!hasDataRef.current) {
        setError(err?.message || null);
      }
    } finally {
      setIsLoading(false);
    }
  }, [groupContractId]);

  // Notifications temps réel du hub collectif (envoi en signature, signature
  // terminée…) : un seul abonnement pour tout le process — chaque notification
  // rafraîchit process + contrat et propage via dataVersion.
  const handleNotification = useCallback(() => {
    refresh();
  }, [refresh]);

  const { memoizedConnect, memoizedDisconnect } = useSignalRNotifications({
    ...GROUP_CONTRACT_NOTIFICATIONS_CONFIG,
    groupId: groupContractId,
    onNotification: handleNotification,
  });

  useEffect(() => {
    memoizedConnect();
    return memoizedDisconnect;
  }, [memoizedConnect, memoizedDisconnect]);

  const buildStepRoute = useCallback(
    (step: GroupContractStepIdentifier) => buildProcessRoute(groupContractId, step, routePrefix),
    [groupContractId, routePrefix],
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({
      groupContractId,
      contractIdentifier: contract?.contractIdentifier ?? null,
      process,
      contract,
      isLocked: contract?.isLocked === true,
      isLoading,
      error,
      dataVersion,
      refresh,
      buildStepRoute,
    }),
    [groupContractId, contract, process, isLoading, error, dataVersion, refresh, buildStepRoute],
  );

  return (
    <GroupContractProcessContext.Provider value={value}>
      {children}
    </GroupContractProcessContext.Provider>
  );
};
